// =============================================================================
// LISTING ROLE CORNERSTONES — the per-POSTING extracted cornerstone-axis store
// (FIX-E, docs/design/fix-e-extraction-2026-07-21.md; WALK-FINDINGS W12/W13).
//
// WHY THIS EXISTS: AxisLensService (AXIS-P1) reads a posting against the FIXED,
// shared ~10-row role_axes taxonomy via literal cue-match. A real posting's own
// prose rarely contains the seed cues for more than one axis, so a rich
// lake-ingested role (e.g. an Anthropic posting) rendered "Strategy vision 15% /
// Untraced 85%" — ONE metric, an empty hexagon. The owner's spec (verbatim):
// "one is not enough we need at least 6 points for our graph". This table holds
// the POSITION's OWN cornerstone shape, extracted straight from the real
// posting text (RoleCornerstoneExtractionService) — the P4 derivation
// AxisLensService.ts's header explicitly defers, built as a per-position read
// rather than a global-taxonomy growth (which would bloat the shared table AND
// still not guarantee ≥6 axes PER POSITION).
//
// RELATION TO listing_axis_reads: a SEPARATE, additive table — listing_axis_reads
// stays byte-identical (AxisLensService untouched, still feeds the tailor
// achievement-ranking that keys on role_axes.axis_key). The serve path composes
// the two (chooseServedAxisRead: whichever produced MORE grounded axes wins the
// radar). Both reads are equally grounded; the cornerstone read is
// position-specific and richer, so it wins the graph whenever it is fuller.
//
// KEYED BY canonicalUrl (mirrors listing_axis_reads.ts's own identity choice —
// "the read is a property of the POSTING, not the viewer"): computed ONCE per
// listing, shared across every user who ever views that posting. UNIQUE =
// store-once (RoleCornerstoneExtractionService.getOrDeriveListingCornerstones
// checks this table FIRST, derives + inserts only when absent).
//
// NO INVENTION (CLAUDE.md foundational law, code-enforced): every cornerstone in
// a served row carries a `sourceSpan` — a VERBATIM quote from the posting the
// extraction traced this axis to. The grounding gate
// (RoleCornerstoneExtractionService.groundCornerstones) drops any cornerstone
// whose sourceSpan does not literally occur in the posting text (reusing
// AxisLensService.cueMatchesText — the SAME substring primitive the taxonomy
// trace gate uses). A cornerstone without a traceable sourceSpan can never be
// stored or served; the count of survivors may be < 6 (an honest thin read),
// NEVER padded up to 6.
//
// ADDITIVE ONLY (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool
// (docs/design/fix-e-extraction-2026-07-21.md's DDL — NOT applied by this lane),
// mirrored here in Drizzle and exported from index.ts.
// =============================================================================

import { sql } from 'drizzle-orm';
import { index, integer, jsonb, pgTable, real, text, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** One extracted cornerstone axis for a single posting. `axisKey` is a
 *  deterministic slug of `label` (RoleCornerstoneExtractionService.slugifyAxisLabel)
 *  — a stable key the FE humanizes back for display, DISTINCT from
 *  role_axes.axis_key (an extracted slug is the posting's own, never a taxonomy
 *  row). `weight` is 0..1 (how central this cornerstone is to the role, the
 *  extraction's own judgment, clamped). `sourceSpan` is the NO-INVENTION
 *  traceability record: the VERBATIM posting quote this axis was traced to — a
 *  cornerstone whose span does not literally occur in the posting never survives
 *  the grounding gate, so a stored sourceSpan is always a real substring of the
 *  posting text as it was read. */
export interface RoleCornerstone {
  axisKey: string;
  label: string;
  weight: number;
  sourceSpan: string;
}

export const listingRoleCornerstones = pgTable(
  'listing_role_cornerstones',
  {
    id: id(),
    /** The posting's stable cross-user identity — see file header. UNIQUE:
     *  one extracted read per listing (store-once). */
    canonicalUrl: text('canonical_url').notNull().unique(),
    /** The grounded cornerstone axes — RoleCornerstone[], sorted highest-weight
     *  first (same convention AxisLensService's weights[] carry). Only
     *  cornerstones whose sourceSpan literally occurs in the posting are ever
     *  present (the grounding gate drops the rest); NEVER padded to a target
     *  count. */
    cornerstones: jsonb('cornerstones')
      .$type<RoleCornerstone[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    /** 'intern' | 'early' | 'mid' | 'senior' | 'exec' — the seniority READING
     *  of this posting, derived via AxisLensService.deriveAxisReadRegister
     *  (reused, not reimplemented — the register concept is shared with the
     *  taxonomy read). */
    register: varchar('register', { length: 16 }).notNull(),
    /** 0..1 — deterministic coverage of the posting's own readable text claimed
     *  by a grounded cornerstone span (AxisLensService.computeCoverage, reused).
     *  Never the model's self-report. */
    coverage: real('coverage').notNull(),
    /** The honest served note ('reads_unusual' | 'jd_too_thin' | 'extracted_read'
     *  | null). 'extracted_read' names the provenance: this shape came from
     *  per-position extraction, not the taxonomy cue-match. Null on a normal,
     *  well-covered extracted read. */
    coverageNote: varchar('coverage_note', { length: 32 }),
    /** WHICH text source the extraction + coverage read for this posting — the
     *  JD-source seam vocabulary reused from AxisLensService.classifyJdSource
     *  ('requirements' | 'description' | 'absent'). NULL only on a legacy row
     *  (never back-guessed — NO INVENTION). */
    sourceKind: varchar('source_kind', { length: 16 }),
    /** How many grounded cornerstones this row serves (= cornerstones.length).
     *  Denormalized for a cheap "how many roles cleared the ≥6 target" telemetry
     *  read without unpacking the jsonb; never a source of truth over the array
     *  itself. */
    extractedCount: integer('extracted_count').notNull().default(0),
    /** Provenance: the resolved model id the extraction ran on (ai_call_log
     *  carries the full cost row; this is the at-a-glance stamp on the read). */
    model: varchar('model', { length: 80 }),
    /** Bumped when the extraction prompt/gate changes in a way that makes a
     *  fresh extraction meaningful — the re-derive key, so a later prompt
     *  revision never silently reinterprets an old stored read (mirrors
     *  listing_axis_reads.taxonomyVersion's own reasoning). */
    promptVersion: integer('prompt_version').notNull().default(1),
    ...timestamps,
  },
  (t) => ({
    canonicalUrlIdx: index('listing_role_cornerstones_canonical_url_idx').on(t.canonicalUrl),
  }),
);
